import { useCallback, useRef, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { courseSlug, type Assignment, type AssignmentStatus, type Course } from '@app/shared'

import { LinkifiedText } from '../LinkifiedText'
import { PeekClose } from '../calendar/icons'
import {
  anchorOfElement,
  useAnchoredPlacement,
  useDismissable,
  useEntered,
  type PeekAnchor,
} from '../../lib/popover'
import {
  assignmentKindLabel,
  assignmentStatusLabel,
  dueCellLabel,
  gradingTypeLabel,
  lockLabel,
  pointsLabel,
  submissionTypesLabel,
} from './derive'

// ============================================================================
// THE ASSIGNMENT DETAIL PEEK — owner request, 2026-08-29: "assignment items are clickable, with
// full bCourses detail". The deadline rows were inert. You could see that Reading response 2
// existed and when it was due, and to learn what it ASKED FOR you opened bCourses.
//
// EVERYTHING HERE WAS ALREADY STORED, with one exception. Points, grading type, submission types,
// the lock date, the status, the score and the bCourses permalink have been columns on
// `assignments` since the connector shipped and on the wire since `AssignmentSchema` was written;
// no screen had anywhere to put them. The exception is the DESCRIPTION, which is new
// (supabase/migrations/20260829094500_assignment_description.sql) and is the reason this panel is
// worth opening at all.
//
// THE SAME OBJECT AS components/calendar/Peek.tsx, deliberately: same `lib/popover.ts` placement
// (which owns the zoom correction), same light dismiss, same one-frame entrance, same
// `role="dialog"` WITHOUT `aria-modal` — a look, never an editor, trapping nothing. It is wider
// (300 rather than 252) because it carries prose, and only because of that.
//
// EVERY ABSENCE IS A SENTENCE OR AN OMISSION, NEVER A BLANK. A field Canvas did not state has no
// row (`derive.ts` returns null and the row is not drawn); the description, which is the one the
// reader will actively look for, says in words that there is none rather than leaving a gap that
// reads as a failed load. `assignment.htmlUrl` is null for hand-entered work, and then there is
// no "Open in bCourses" button — a button that goes nowhere is worse than no button.
// ============================================================================

const WIDTH = 300

/**
 * THE OPEN/CLOSE STATE, in one hook because three surfaces open this panel — the course page's
 * assignments table, the deadline runway, and the course card's DUE line — and three copies of
 * "capture the clicked element's rect, remember which row it was" is three places for the anchor
 * to be captured a frame too late.
 *
 * THE RECT IS TAKEN AT CLICK TIME, from the element the click landed on, exactly as the calendar
 * grid does it: after the panel mounts, the row may have scrolled, and a rect read later places
 * the panel beside where the row is NOW rather than where it was pressed.
 */
export function useAssignmentPeek() {
  const [open, setOpen] = useState<{ assignment: Assignment; anchor: PeekAnchor } | null>(null)

  const openFor = useCallback((assignment: Assignment, target: Element | null) => {
    if (!target) return
    setOpen((current) =>
      // A second click on the SAME row closes it. The row is the toggle; there is no other way
      // to dismiss it from the keyboard than Escape, and one gesture doing both is the idiom the
      // announcement feed already uses.
      current?.assignment.id === assignment.id
        ? null
        : { assignment, anchor: anchorOfElement(target) },
    )
  }, [])

  const close = useCallback(() => setOpen(null), [])

  return { open, openFor, close }
}

/** How tall the description may grow before it scrolls inside the panel. ~9 lines. */
const MAX_DESCRIPTION_HEIGHT = '10.5rem'

const STATUS_PILL: Record<AssignmentStatus, string> = {
  missing: 'bg-danger-line-2 text-danger-2',
  upcoming: 'bg-surface-4 text-muted',
  submitted: 'bg-info-fill text-info',
  graded: 'bg-success-fill text-success',
}

export function AssignmentPeek({
  assignment,
  course,
  anchor,
  onClose,
}: {
  assignment: Assignment
  /** Only used for the DUE line's "in class" derivation. Absent on the overview, and fine. */
  course: Course | null | undefined
  anchor: PeekAnchor
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const entered = useEntered()
  const placement = useAnchoredPlacement(ref, anchor, WIDTH)
  useDismissable(ref, onClose)

  const kind = assignmentKindLabel(assignment.kind)
  const lock = lockLabel(assignment)
  const points = pointsLabel(assignment)
  const grading = gradingTypeLabel(assignment)
  const handIn = submissionTypesLabel(assignment)
  const description = assignment.description.trim()

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={assignment.title}
      data-testid="assignment-peek"
      className={[
        'fixed z-20 flex flex-col gap-[7px] rounded-panel border border-line-2 bg-surface p-[13px] shadow-lifted',
        // The motion system's panel beat, the same pair the calendar peek settles on.
        'transition-[opacity,transform] duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
        entered ? 'opacity-100' : 'opacity-0',
      ].join(' ')}
      style={{
        width: WIDTH,
        left: placement.left,
        top: placement.top,
        transform: entered ? 'translateY(0)' : 'translateY(-4px)',
      }}
    >
      <div className="flex items-start gap-[10px]">
        <span className="min-w-0 flex-1 text-pretty text-[12.5px] font-semibold leading-[1.3] text-ink">
          {assignment.title}
        </span>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
          className="mo-ink mo-press flex-none rounded-pill p-[3px] text-faintest-2 hover:text-ink-2"
        >
          <PeekClose />
        </button>
      </div>

      <div className="flex items-center gap-[7px]">
        <span className="text-[11px] font-medium leading-none text-ink-3">
          {assignment.courseCode}
        </span>
        <span
          className={[
            'rounded-pill px-[8px] py-[3px] text-[10px] font-semibold leading-none',
            STATUS_PILL[assignment.status],
          ].join(' ')}
        >
          {assignmentStatusLabel(assignment)}
        </span>
        {/* 'unclassified' renders as an em dash everywhere else in School; here there is room for
            the reason, so the chip is simply not drawn rather than drawn as a dash nobody can
            interpret out of a table's context. */}
        {kind !== '—' && (
          <span className="text-[10.5px] font-normal leading-none text-microcopy">{kind}</span>
        )}
      </div>

      <dl className="flex flex-col gap-[5px]">
        <Fact label="Due">{dueCellLabel(assignment, course)}</Fact>
        {lock && <Fact label="Locks">{lock}</Fact>}
        {points && <Fact label="Points">{points}</Fact>}
        {grading && <Fact label="Graded">{grading}</Fact>}
        {handIn && <Fact label="Hand in">{handIn}</Fact>}
      </dl>

      <div
        className="overflow-y-auto border-t border-line pt-[8px]"
        style={{ maxHeight: MAX_DESCRIPTION_HEIGHT }}
      >
        {description ? (
          // `whitespace-pre-line` keeps the professor's paragraphs; the text arrived already
          // stripped of Canvas's markup on the Worker (lib/canvas.ts → toAssignmentRow), and the
          // URLs inside it become real links here by the same linkifier the announcements use.
          <LinkifiedText
            text={description}
            className="block whitespace-pre-line text-pretty text-[11px] font-normal leading-[1.5] text-ink-3"
          />
        ) : (
          <p className="text-[11px] font-normal leading-[1.45] text-microcopy">
            Canvas carries no description for this assignment — the professor gave it a title and a
            date and nothing else.
          </p>
        )}
      </div>

      <div className="mt-[2px] flex flex-wrap items-center gap-[7px]">
        {assignment.htmlUrl && (
          <a
            href={assignment.htmlUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
            className="mo-ink mo-press rounded-context border border-line px-[10px] py-[5px] text-[10px] font-semibold leading-none text-ink-2"
          >
            Open in bCourses
          </a>
        )}
        {assignment.courseCode && (
          <Link
            to={`/school/${courseSlug(assignment.courseCode)}`}
            style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
            className="mo-ink mo-press rounded-context border border-line px-[10px] py-[5px] text-[10px] font-semibold leading-none text-ink-2"
          >
            Open in Course
          </Link>
        )}
      </div>
    </div>
  )
}

/** One labelled fact. A `<dl>` row, because that is what a list of label/value pairs is. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-[8px]">
      <dt className="w-[52px] flex-none text-[9.5px] font-semibold uppercase leading-none tracking-[0.07em] text-microcopy">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-[11px] font-medium leading-[1.35] text-ink-2">
        {children}
      </dd>
    </div>
  )
}
